import type { AppState } from '@auth0/auth0-react';
import { RouterPaths } from './RouterPaths';

const MAX_RETURN_PATH_LENGTH = 2048;
const { Login, Logout, Root } = RouterPaths.BasePages;

/**
 * Returns a path+search+hash safe for same-app client-side redirect, or undefined.
 * Rejects `//`, non-path inputs, login/logout targets, and overlong strings.
 */
export function sanitizeInternalReturnPath(raw: string | null | undefined): string | undefined {
    if (!raw || typeof raw !== 'string') {
        return undefined;
    }
    const trimmed = raw.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
        return undefined;
    }
    if (trimmed.length > MAX_RETURN_PATH_LENGTH) {
        return undefined;
    }

    try {
        const resolved = new URL(trimmed, window.location.origin);
        const pathWithQueryHash = `${resolved.pathname}${resolved.search}${resolved.hash}`;

        if (pathWithQueryHash === Login || pathWithQueryHash.startsWith(`${Login}/`) || pathWithQueryHash.startsWith(`${Login}?`)) {
            return undefined;
        }
        if (pathWithQueryHash === Logout || pathWithQueryHash.startsWith(`${Logout}/`) || pathWithQueryHash.startsWith(`${Logout}?`)) {
            return undefined;
        }

        return pathWithQueryHash;
    } catch {
        return undefined;
    }
}

/** Sanitized in-app path to return to after login, or undefined for default landing. */
export function getPostLoginReturnTarget(): string | undefined {
    const { pathname, search = '' } = window?.location || {};

    if (!pathname) {
        return undefined;
    }

    if (pathname === Login) {
        const raw = new URLSearchParams(search).get('returnUrl');
        return typeof raw === 'string' ? sanitizeInternalReturnPath(raw) : undefined;
    }

    const fromLocation = sanitizeInternalReturnPath(`${pathname}${search}`);

    if (fromLocation === Root) {
        return undefined;
    }

    return fromLocation;
}

export function getAuth0Path(): string {
    const returnTarget = getPostLoginReturnTarget();

    if (returnTarget) {
        return `${Login}?${new URLSearchParams({ returnUrl: returnTarget }).toString()}`;
    }

    return Login;
}

export function getAuth0RedirectUri(): string {
    return `${window.location.origin}${getAuth0Path()}`;
}

/** Options for `loginWithRedirect`: pass `appState` so return path survives Auth0 callback even if `returnUrl` is stripped from the URL. */
export function getAuth0LoginWithRedirectOptions(): { redirectUri: string; appState?: AppState } {
    const redirectUri = getAuth0RedirectUri();
    const returnTo = getAuth0Path();
    return { redirectUri, appState: { returnTo } };
}
